import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { Child, Chore, Payout, Completion, Settings } from '@shared/schema';
import { nanoid } from 'nanoid';

interface ChoresDB extends DBSchema {
  children: {
    key: string;
    value: Child;
  };
  chores: {
    key: string;
    value: Chore;
  };
  payouts: {
    key: string;
    value: Payout;
  };
  completions: {
    key: string;
    value: Completion;
  };
  settings: {
    key: string;
    value: Settings;
  };
}

let dbInstance: IDBPDatabase<ChoresDB> | null = null;
let dbPromise: Promise<IDBPDatabase<ChoresDB>> | null = null;
let isSeeding = false;

export async function getDB(): Promise<IDBPDatabase<ChoresDB>> {
  if (dbInstance) {
    return dbInstance;
  }
  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = (async () => {
    const db = await openDB<ChoresDB>('chores-rewards-db', 3, {
      upgrade(db, oldVersion) {
        // Children store
        if (!db.objectStoreNames.contains('children')) {
          db.createObjectStore('children', { keyPath: 'id' });
        }

        // Chores store
        if (!db.objectStoreNames.contains('chores')) {
          db.createObjectStore('chores', { keyPath: 'id' });
        }

        // Payouts store
        if (!db.objectStoreNames.contains('payouts')) {
          db.createObjectStore('payouts', { keyPath: 'id' });
        }

        // Completions store (added in v3): one row per chore completion, so
        // "N done" counts can be derived instead of estimated. The contains()
        // guard means an existing v1/v2 database only gains this store; the
        // children/chores/payouts/settings stores it already has are left
        // untouched and their data survives the upgrade.
        if (!db.objectStoreNames.contains('completions')) {
          db.createObjectStore('completions', { keyPath: 'id' });
        }

        // Settings store
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings');
        }

        // Note: Migration removed - users must delete IndexedDB to upgrade
        // The favoriteChoreIds field is now added by default when creating children
      },
      blocked() {
        // Another tab has the database open with a different version
        // Nothing to do here in this context; open will resolve once unblocked
      },
      blocking() {
        // If another connection is open, close this one to allow upgrade
        try { db.close(); } catch {}
      },
      terminated() {
        // The connection was unexpectedly closed; let next getDB() reopen
      }
    });

    // Ensure we close on future version changes to avoid upgrade deadlocks
    db.onversionchange = () => {
      try { db.close(); } catch {}
    };

    // Wait a tick to ensure upgrade transaction commit completes
    await new Promise((resolve) => setTimeout(resolve, 0));

    // Seed default chores if none exist (only once and not concurrently)
    if (!isSeeding) {
      isSeeding = true;
      try {
        await seedDefaultChores(db);
      } finally {
        isSeeding = false;
      }
    }

    dbInstance = db;
    return db;
  })();

  try {
    const result = await dbPromise;
    return result;
  } finally {
    // Clear the promise after resolution to allow future reopens if closed
    dbPromise = null;
  }
}

async function seedDefaultChores(db: IDBPDatabase<ChoresDB>): Promise<void> {
  const existingChores = await db.getAll('chores');
  if (existingChores.length === 0) {
    const now = new Date();
    const defaultChores: Chore[] = [
      { id: nanoid(), title: "Vacuum inc edges & couch cushions", valueCents: 500, createdAt: now },
      { id: nanoid(), title: "Mop House", valueCents: 500, createdAt: now },
      { id: nanoid(), title: "Clean room", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Make dinner", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Fix couch up", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Feed Missy", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Fill Missy's water bowl", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Febreeze house", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Put washing in machine", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Take rubbish", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Put clothes away", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Wipe down bathroom", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Dusting surfaces", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Tidy play room", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Putting shopping away", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Cleaning up after self", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Filling dishwasher", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Empty dishwasher", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Cleaning kitchen", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Making bed", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Shower", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Read a book (Over 40 pages)", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Something kind for someone else", valueCents: 100, createdAt: now },
      { id: nanoid(), title: "Not spending money for a week", valueCents: 500, createdAt: now }
    ];

    const tx = db.transaction('chores', 'readwrite');
    const store = tx.objectStore('chores');
    
    for (const chore of defaultChores) {
      await store.add(chore);
    }
    
    await tx.done;
  }
}

export async function closeDB(): Promise<void> {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}
